// ── LearnPet System Constants ─────────────────────────────────────────────────
// 6 Indian animals × 5 evolution stages × 8 moods

export const PET_TYPES = [
  {
    id: 'elephant',
    species: 'Baby Elephant',
    tagline: 'Gentle and wise, remembers everything',
    personality: 'The gentle giant who never forgets',
    signatureMove: 'Trunk trumpet celebration',
    appealsTo: 'Thoughtful, careful learners',
    color: '#A8D5BA',
  },
  {
    id: 'squirrel',
    species: 'Squirrel',
    tagline: 'Quick and curious, always exploring',
    personality: 'The hyperactive explorer',
    signatureMove: 'Cheek-stuffing when learning new facts',
    appealsTo: 'Energetic kids who get bored easily',
    color: '#F4A460',
  },
  {
    id: 'peacock',
    species: 'Peacock',
    tagline: 'Colorful and creative, sees beauty everywhere',
    personality: 'The artist who finds patterns in everything',
    signatureMove: 'Tail fan opening with feathers lighting up',
    appealsTo: 'Creative, artistic children',
    color: '#4ECDC4',
  },
  {
    id: 'bear',
    species: 'Bear Cub',
    tagline: 'Strong and brave, always has your back',
    personality: 'The protector who gives the best hugs',
    signatureMove: 'Bear hug when you need encouragement',
    appealsTo: 'Shy kids who need confidence',
    color: '#8B7355',
  },
  {
    id: 'parrot',
    species: 'Parrot',
    tagline: 'Chatty and smart, never forgets a fact',
    personality: 'The memory champion who repeats key concepts',
    signatureMove: 'Flies across screen repeating the key fact',
    appealsTo: 'Social, talkative learners',
    color: '#7CB342',
  },
  {
    id: 'tiger',
    species: 'Tiger Cub',
    tagline: 'Fearless and fierce, loves a challenge',
    personality: 'The challenger who tackles tough problems',
    signatureMove: 'Victory roar with screen shake',
    appealsTo: 'Competitive, ambitious kids',
    color: '#FF8C42',
  },
];

// Stars needed to REACH each stage (cumulative total)
export const EVOLUTION_STAGES = [
  { stage: 1, name: 'Baby',      totalStars: 0   },
  { stage: 2, name: 'Curious',   totalStars: 20  },
  { stage: 3, name: 'Playful',   totalStars: 80  },
  { stage: 4, name: 'Wise',      totalStars: 200 },
  { stage: 5, name: 'Legendary', totalStars: 400 },
];

export const STAR_REWARDS = {
  storyCompleted:  1,
  quizPassed:      1,   // 3+ / 4 correct
  quizPerfect:     2,   // 4 / 4 correct (replaces quizPassed)
  streakBonus7Day:  5,
  streakBonus30Day: 15,
};

export const PET_MOODS = {
  happy:      { emoji: '😊', message: (name) => `${name} is happy to see you!` },
  excited:    { emoji: '🤩', message: (name) => `${name} can't wait to learn!` },
  curious:    { emoji: '🤔', message: (name) => `${name} is wondering what we'll learn today...` },
  sleepy:     { emoji: '😴', message: (name) => `${name} is getting sleepy. One more story?` },
  lonely:     { emoji: '🥺', message: (name) => `${name} missed you! Let's learn something!` },
  sleeping:   { emoji: '💤', message: (name) => `Shhh... ${name} fell asleep waiting for you. Tap to wake up!` },
  celebrating:{ emoji: '🎉', message: (name) => `${name} is so proud of you!` },
  encouraging:{ emoji: '💪', message: (name) => `${name} believes in you!` },
  thinking:   { emoji: '🤔', message: (name) => `${name} is thinking hard...` },
  listening:  { emoji: '🎧', message: (name) => `${name} is listening with you!` },
};

/**
 * Returns age-calibrated pet messages.
 * @param {number} classNum  - 1–8
 * @param {string} petName   - child-chosen name
 * @param {{ starsToNext: number }} context
 */
export function getPetMessage(classNum, petName, context = {}) {
  const { starsToNext = 0 } = context;
  const n = Number(classNum) || 6;

  if (n <= 2) {
    return {
      greeting:    `${petName} is happy! 😊`,
      encourage:   `You can do it! 💪`,
      evolveClose: `${petName} wants to grow! ⭐`,
      celebration: `YAY! ${petName} is dancing! 🎉`,
    };
  }
  if (n <= 4) {
    return {
      greeting:    `${petName} was waiting for you! Let's learn something fun!`,
      encourage:   `Hmm, tricky one! Let's think together...`,
      evolveClose: `Just ${starsToNext} more stars and ${petName} will evolve!`,
      celebration: `Amazing! ${petName} is SO proud of you!`,
    };
  }
  if (n <= 6) {
    return {
      greeting:    `Hey! ${petName} has been curious about today's topic.`,
      encourage:   `That's a tough one. Take your time, no rush.`,
      evolveClose: `${starsToNext} stars to go. ${petName} can feel the evolution coming!`,
      celebration: `Brilliant! ${petName} high-fives you! 🙌`,
    };
  }
  return {
    greeting:    `${petName} is ready when you are.`,
    encourage:   `Think it through. You've got this.`,
    evolveClose: `${starsToNext} stars to next evolution.`,
    celebration: `Nailed it. ${petName} nods with respect. 👊`,
  };
}

/**
 * Derive pet stage from total star count.
 * @param {number} totalStars
 * @returns {number} stage 1–5
 */
export function getStageFromStars(totalStars) {
  const stars = Number(totalStars) || 0;
  let stage = 1;
  for (const s of EVOLUTION_STAGES) {
    if (stars >= s.totalStars) stage = s.stage;
  }
  return stage;
}

/**
 * How many stars until the next evolution.
 * Returns 0 if already at Legendary.
 * @param {number} totalStars
 * @returns {number}
 */
export function starsToNextStage(totalStars) {
  const stars = Number(totalStars) || 0;
  const next = EVOLUTION_STAGES.find(s => s.totalStars > stars);
  return next ? next.totalStars - stars : 0;
}

/**
 * Compute pet mood from hours since last session.
 * @param {string|null} lastSessionAt - ISO timestamp string
 * @returns {string} mood key
 */
export function computePetMood(lastSessionAt) {
  if (!lastSessionAt) return 'curious';
  const hours = (Date.now() - new Date(lastSessionAt).getTime()) / 3_600_000;
  if (hours < 1)  return 'celebrating';
  if (hours < 12) return 'happy';
  if (hours < 24) return 'excited';
  if (hours < 48) return 'curious';
  if (hours < 72) return 'lonely';
  return 'sleeping';
}
